/* Connector brand icons + generic custom-connector icons + a URL-aware renderer.
 * Kept in its own module so ConnectorsModal, the chat flyout, and the custom
 * connector sub-modals can all share it without circular imports. */
import {
  GoogleDriveLogo,
  OneDriveLogo,
} from '@/components/icons/CloudServiceLogo';

import { BUILTIN_MCP_CONNECTORS } from '@/constants/builtin-mcp-connectors';

// Built-in MCP connectors use the favicon service (like Claude's directory) for
// real brand logos with zero maintenance, instead of hand-drawn marks.
const favicon =
  (domain: string) =>
  (className: string): JSX.Element => (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
      alt={domain}
      className={`${className} rounded object-contain`}
    />
  );

/** Google Chrome logo (flat colours). Geometry: outer r=24, white ring r=12,
 * blue core r=9.5; the three sector edges are tangent to the white ring. */
export function ChromeIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox='0 0 48 48' className={className} aria-hidden='true'>
      <path
        d='M24 12h20.7846A24 24 0 0 0 3.2154 12l10.3923 18A12 12 0 0 1 24 12Z'
        fill='#EA4335'
      />
      <path
        d='M34.3923 30 24 48a24 24 0 0 0 20.7846-36H24a12 12 0 0 1 10.3923 18Z'
        fill='#FBBC04'
      />
      <path
        d='M13.6077 30 3.2154 12A24 24 0 0 0 24 48l10.3923-18a12 12 0 0 1-20.7846 0Z'
        fill='#34A853'
      />
      <circle cx='24' cy='24' r='12' fill='#fff' />
      <circle cx='24' cy='24' r='9.5' fill='#1A73E8' />
    </svg>
  );
}

export const CONNECTOR_ICONS: Record<
  string,
  (className: string) => JSX.Element
> = {
  // "My Browser" (the Easy-Peasy Browser Agent Chrome extension) — Chrome's
  // own mark: the extension ships on the Chrome Web Store, and it is the logo
  // people look for (Edge/Brave/Arc users install it from the same listing).
  'my-browser': (cls) => <ChromeIcon className={cls} />,
  gmail: (cls) => (
    <svg viewBox='0 0 16 16' className={cls}>
      <path
        d='M1.95455 13.2527H4.18182V7.84362L1 5.45725V12.2982C1 12.8263 1.42795 13.2527 1.95455 13.2527Z'
        fill='#4285F4'
      />
      <path
        d='M11.8184 13.2527H14.0456C14.5738 13.2527 15.0002 12.8248 15.0002 12.2982V5.45725L11.8184 7.84362'
        fill='#34A853'
      />
      <path
        d='M11.8184 3.70725V7.84362L15.0002 5.45725V4.18453C15.0002 3.00407 13.6527 2.33112 12.7093 3.03907'
        fill='#FBBC04'
      />
      <path
        d='M4.18164 7.84362V3.70725L7.99982 6.57089L11.818 3.70725V7.84362L7.99982 10.7073'
        fill='#EA4335'
      />
      <path
        d='M1 4.18453V5.45725L4.18182 7.84362V3.70725L3.29091 3.03907C2.34591 2.33112 1 3.00407 1 4.18453Z'
        fill='#C5221F'
      />
    </svg>
  ),
  'google-calendar': (cls) => (
    <svg viewBox='0 0 16 16' className={cls}>
      <path
        d='M11.4211 4.57892L8.34215 4.23682L4.57898 4.57892L4.23682 8L4.57891 11.4211L7.99999 11.8487L11.4211 11.4211L11.7632 7.91452L11.4211 4.57892Z'
        fill='white'
      />
      <path
        d='M5.98255 9.88669C5.72684 9.71392 5.54978 9.46166 5.45312 9.12808L6.0467 8.88348C6.10059 9.08875 6.19464 9.24781 6.32893 9.36071C6.46238 9.47362 6.62488 9.52919 6.81474 9.52919C7.0089 9.52919 7.17569 9.47017 7.31505 9.35213C7.45441 9.23409 7.52461 9.08355 7.52461 8.90142C7.52461 8.715 7.45103 8.56271 7.30393 8.44473C7.15684 8.32676 6.97211 8.26767 6.75143 8.26767H6.40849V7.68014H6.71633C6.9062 7.68014 7.06617 7.62885 7.19617 7.52622C7.32617 7.42358 7.39117 7.28331 7.39117 7.10456C7.39117 6.94551 7.33299 6.81889 7.2167 6.72399C7.10042 6.62909 6.95326 6.58118 6.77451 6.58118C6.60005 6.58118 6.46147 6.6274 6.35883 6.72061C6.2562 6.81382 6.18177 6.92841 6.13478 7.06355L5.54724 6.81895C5.62505 6.59828 5.76792 6.40328 5.97742 6.2348C6.18698 6.06632 6.45464 5.98162 6.77964 5.98162C7.01995 5.98162 7.23633 6.02784 7.42795 6.12105C7.61951 6.21426 7.77005 6.34341 7.87867 6.5076C7.98728 6.67264 8.04117 6.85743 8.04117 7.06264C8.04117 7.2722 7.99072 7.44919 7.88978 7.5946C7.78883 7.74 7.66481 7.85115 7.51772 7.92902V7.96406C7.71187 8.04531 7.87008 8.16933 7.99495 8.33612C8.11897 8.50291 8.18137 8.7022 8.18137 8.93483C8.18137 9.16747 8.12235 9.37527 8.00431 9.55747C7.88627 9.73966 7.72292 9.88331 7.51596 9.98764C7.30816 10.092 7.07468 10.145 6.81552 10.145C6.51535 10.1458 6.23826 10.0595 5.98255 9.88669Z'
        fill='#1A73E8'
      />
      <path
        d='M9.62503 6.94121L8.97672 7.41246L8.65088 6.91814L9.82003 6.07483H10.2682V10.0526H9.62503V6.94121Z'
        fill='#1A73E8'
      />
      <path
        d='M11.421 14.5L14.4999 11.4211L12.9605 10.7369L11.421 11.4211L10.7368 12.9605L11.421 14.5Z'
        fill='#EA4335'
      />
      <path
        d='M3.89453 12.9605L4.57872 14.5H11.4208V11.4211H4.57872L3.89453 12.9605Z'
        fill='#34A853'
      />
      <path
        d='M2.52628 1.5C1.95929 1.5 1.5 1.95929 1.5 2.52628V11.421L3.03946 12.1052L4.57892 11.421V4.57892H11.421L12.1052 3.03946L11.4211 1.5H2.52628Z'
        fill='#4285F4'
      />
      <path
        d='M1.5 11.4211V13.4737C1.5 14.0408 1.95929 14.5 2.52628 14.5H4.57892V11.4211H1.5Z'
        fill='#188038'
      />
      <path
        d='M11.4209 4.57892V11.421H14.4998V4.57892L12.9604 3.89473L11.4209 4.57892Z'
        fill='#FBBC04'
      />
      <path
        d='M14.4998 4.57892V2.52628C14.4998 1.95922 14.0405 1.5 13.4735 1.5H11.4209V4.57892H14.4998Z'
        fill='#1967D2'
      />
    </svg>
  ),
  'google-drive': (cls) => <GoogleDriveLogo className={cls} />,
  github: (cls) => (
    <svg role='img' viewBox='0 0 24 24' className={cls} fill='currentColor'>
      <path d='M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12' />
    </svg>
  ),
  // Official Canva logo (gradient circle + white "C"). Vendored into /public so
  // there's no runtime dependency on Canva's CDN; transparent PNG scales cleanly
  // at icon sizes.
  canva: (cls) => (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src='/images/connectors/canva.png'
      alt='Canva'
      className={`${cls} object-contain`}
    />
  ),
  posthog: (cls) => (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src='/images/connectors/posthog.svg'
      alt='PostHog'
      className={`${cls} object-contain`}
    />
  ),
  supabase: (cls) => (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src='/images/connectors/supabase.svg'
      alt='Supabase'
      className={`${cls} object-contain`}
    />
  ),
  crisp: (cls) => (
    <svg viewBox='0 0 24 24' className={cls} fill='none'>
      <path
        d='M3.6 3.4 20 2.1c.9-.07 1.7.6 1.77 1.5l.83 10.6c.07.9-.6 1.7-1.5 1.77l-6.9.54-3.55 3.79a.7.7 0 0 1-1.2-.38l-.5-3.32-4.2.33a1.65 1.65 0 0 1-1.77-1.5L2.1 5.17c-.07-.9.6-1.7 1.5-1.77Z'
        fill='#1972F5'
      />
    </svg>
  ),
  notion: (cls) => (
    <svg viewBox='0 0 335 350' className={cls} fill='none'>
      <path
        d='M21.056 15.0565L214.701 0.796296C238.481 -1.24326 244.599 0.122938 259.545 10.9802L321.359 54.4257C331.558 61.8966 334.958 63.9307 334.958 72.0749V310.358C334.958 325.292 329.518 334.124 310.498 335.475L85.6192 349.054C71.3416 349.735 64.5464 347.701 57.0693 338.19L11.5487 279.13C3.3919 268.259 0 260.126 0 250.61V38.8077C0 26.5954 5.44153 16.4088 21.056 15.0565Z'
        fill='white'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M214.701 0.796296L21.056 15.0565C5.44153 16.4088 0 26.5954 0 38.8077V250.61C0 260.126 3.3919 268.259 11.5487 279.13L57.0693 338.19C64.5464 347.701 71.3416 349.735 85.6192 349.054L310.498 335.475C329.518 334.124 334.958 325.292 334.958 310.358V72.0749C334.958 64.3554 331.904 62.1254 322.899 55.5518C322.404 55.1901 321.891 54.8153 321.359 54.4257L259.545 10.9802C244.599 0.122938 238.481 -1.24326 214.701 0.796296ZM90.718 68.1664C72.3581 69.4062 68.1862 69.6879 57.7599 61.2177L31.2457 40.1737C28.5415 37.4525 29.8978 34.0579 36.6871 33.3847L222.855 19.8061C238.478 18.4455 246.629 23.8852 252.747 28.635L284.678 51.7183C286.038 52.3944 289.429 56.4624 285.351 56.4624L93.0882 68.0071C92.2729 68.0614 91.4837 68.1147 90.719 68.1664L90.718 68.1664ZM69.3031 308.318V106.024C69.3031 97.2006 72.021 93.1245 80.1694 92.4398L300.977 79.5458C308.466 78.867 311.857 83.6223 311.857 92.4398V293.376C311.857 302.208 310.495 309.686 298.261 310.358L86.9643 322.584C74.7361 323.257 69.3031 319.19 69.3031 308.318ZM277.894 116.876C279.249 122.992 277.894 129.102 271.768 129.789L261.586 131.817V281.164C252.747 285.915 244.596 288.631 237.804 288.631C226.929 288.631 224.206 285.234 216.06 275.056L149.467 170.513V271.662L170.539 276.417C170.539 276.417 170.539 288.631 153.538 288.631L106.67 291.349C105.308 288.631 106.67 281.848 111.424 280.488L123.654 277.099V143.362L106.672 142.001C105.311 135.886 108.703 127.068 118.221 126.383L168.501 122.994L237.804 228.898V135.212L220.135 133.184C218.778 125.707 224.206 120.278 231.001 119.605L277.894 116.876Z'
        fill='black'
      />
    </svg>
  ),
  // Outlook "M envelope" mark (simplified) shared by Mail; Calendar variant
  // swaps the envelope body for a date grid so the two rows are distinguishable.
  'outlook-mail': (cls) => (
    <svg viewBox='0 0 16 16' className={cls}>
      <rect x='5' y='2' width='10' height='12' rx='1' fill='#1066B5' />
      <rect x='5' y='2' width='10' height='6' rx='1' fill='#32A9E7' />
      <path d='M5 8h10v5a1 1 0 01-1 1H6a1 1 0 01-1-1V8z' fill='#1066B5' />
      <path d='M5 8l5 3.2L15 8v1.4l-5 3.2-5-3.2V8z' fill='#CCE5F5' />
      <rect x='1' y='4.5' width='8' height='7' rx='1' fill='#0F5298' />
      <text
        x='5'
        y='10.3'
        textAnchor='middle'
        fontSize='6'
        fontWeight='bold'
        fill='white'
        fontFamily='Arial, sans-serif'
      >
        O
      </text>
    </svg>
  ),
  'outlook-calendar': (cls) => (
    <svg viewBox='0 0 16 16' className={cls}>
      <rect x='2' y='2.5' width='12' height='11.5' rx='1.5' fill='#1066B5' />
      <rect x='2' y='2.5' width='12' height='3.5' rx='1.5' fill='#32A9E7' />
      <rect x='2' y='4.5' width='12' height='1.5' fill='#32A9E7' />
      <rect x='4.5' y='1' width='1.5' height='3' rx='0.75' fill='#0F5298' />
      <rect x='10' y='1' width='1.5' height='3' rx='0.75' fill='#0F5298' />
      <g fill='white'>
        <rect x='4' y='7.5' width='2' height='2' rx='0.4' />
        <rect x='7' y='7.5' width='2' height='2' rx='0.4' />
        <rect x='10' y='7.5' width='2' height='2' rx='0.4' />
        <rect x='4' y='10.5' width='2' height='2' rx='0.4' />
        <rect x='7' y='10.5' width='2' height='2' rx='0.4' />
        <rect x='10' y='10.5' width='2' height='2' rx='0.4' opacity='0.6' />
      </g>
    </svg>
  ),
  // OneDrive cloud (the Fluent-era logo, from the CC0 svg-logos set).
  onedrive: (cls) => <OneDriveLogo className={cls} />,
  // OneNote mark (simplified, same construction as the Outlook marks): the
  // notebook's tab stripes in OneNote's purples with the "N" plate in front.
  onenote: (cls) => (
    <svg viewBox='0 0 16 16' className={cls}>
      <rect x='5' y='2' width='10' height='12' rx='1' fill='#7719AA' />
      <path d='M6 2h8a1 1 0 011 1v2H5V3a1 1 0 011-1z' fill='#CA64EA' />
      <rect x='5' y='5' width='10' height='3' fill='#AE4BD5' />
      <rect x='5' y='8' width='10' height='3' fill='#9332BF' />
      <rect x='1' y='4.5' width='8' height='7' rx='1' fill='#5E1C87' />
      <text
        x='5'
        y='10.3'
        textAnchor='middle'
        fontSize='6'
        fontWeight='bold'
        fill='white'
        fontFamily='Arial, sans-serif'
      >
        N
      </text>
    </svg>
  ),
  mixpanel: favicon('mixpanel.com'),
  wix: favicon('wix.com'),
  linear: favicon('linear.app'),
  asana: favicon('asana.com'),
  monday: favicon('monday.com'),
  'meta-ads': favicon('meta.com'),
  whatsapp: favicon('whatsapp.com'),
  trello: favicon('trello.com'),
  stripe: favicon('stripe.com'),
  paypal: favicon('paypal.com'),
  square: favicon('squareup.com'),
  webflow: favicon('webflow.com'),
  vercel: favicon('vercel.com'),
  sentry: favicon('sentry.io'),
  klaviyo: favicon('klaviyo.com'),
  mailerlite: favicon('mailerlite.com'),
  customerio: favicon('customer.io'),
  airtable: favicon('airtable.com'),
  clickup: favicon('clickup.com'),
  todoist: favicon('todoist.com'),
  calcom: favicon('cal.com'),
  tally: favicon('tally.so'),
  attio: favicon('attio.com'),
  close: favicon('close.com'),
  cloudinary: favicon('cloudinary.com'),
  neon: favicon('neon.tech'),
  zapier: favicon('zapier.com'),
  ahrefs: favicon('ahrefs.com'),
  semrush: favicon('semrush.com'),
  dataforseo: favicon('dataforseo.com'),
  // Generic icon for user-defined Custom API connectors (a key).
  'custom-api': (cls) => (
    <svg
      viewBox='0 0 24 24'
      className={cls}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z'
      />
    </svg>
  ),
  // Generic icon for user-defined Custom MCP connectors (stacked servers).
  'custom-mcp': (cls) => (
    <svg
      viewBox='0 0 24 24'
      className={cls}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01'
      />
    </svg>
  ),
};

// Maps a chat tool-name prefix to the connector whose brand icon represents it.
const TOOL_PREFIX_TO_CONNECTOR: Record<string, string> = {
  canva_: 'canva',
  gmail_: 'gmail',
  gcal_: 'google-calendar',
  gdrive_: 'google-drive',
  github_: 'github',
  notion_: 'notion',
  outlook_: 'outlook-mail',
  ocal_: 'outlook-calendar',
  onedrive_: 'onedrive',
  onenote_: 'onenote',
};

// Built-in MCP connectors are tool-prefixed `mcp_<slug(id)>_`. Map that service
// id back to the registry connector id (its CONNECTOR_ICONS key) so their tool
// calls render with the right brand icon — registry-driven, so a new connector
// needs no edit here. (Custom-MCP tools are `mcp_<uuidhex>_` and won't match.)
const BUILTIN_MCP_SERVICE_TO_ID: Record<string, string> = Object.fromEntries(
  // Resolve to the entry's `icon` key, not its id: region/scope variants
  // (supabase-rw, mixpanel-eu) share their sibling's icon and have no
  // CONNECTOR_ICONS entry of their own.
  BUILTIN_MCP_CONNECTORS.map((c) => [
    `mcp_${c.id.replace(/-/g, '')}`,
    c.icon || c.id,
  ]),
);

/**
 * Map a chat tool name (and its args) to a connector id whose brand icon should
 * represent it in the UI — e.g. 'canva_search_designs' -> 'canva', or the
 * connector-gateway tool 'use_connector' -> its `service` arg. Returns undefined
 * for non-connector tools (or unknown services) so callers fall back to their
 * own tool icon.
 */
export function getToolConnectorId(
  toolName: string,
  args?: Record<string, unknown>,
): string | undefined {
  if (toolName === 'use_connector') {
    const service = (args as { service?: string } | undefined)?.service;
    if (service && BUILTIN_MCP_SERVICE_TO_ID[service]) {
      return BUILTIN_MCP_SERVICE_TO_ID[service];
    }
    return service && CONNECTOR_ICONS[service] ? service : undefined;
  }
  for (const service in BUILTIN_MCP_SERVICE_TO_ID) {
    if (toolName.startsWith(`${service}_`)) {
      return BUILTIN_MCP_SERVICE_TO_ID[service];
    }
  }
  for (const prefix in TOOL_PREFIX_TO_CONNECTOR) {
    if (toolName.startsWith(prefix)) return TOOL_PREFIX_TO_CONNECTOR[prefix];
  }
  return undefined;
}

const isUrl = (s?: string) =>
  !!s && (s.startsWith('http://') || s.startsWith('https://'));

/**
 * Render a connector icon. `icon` may be a CONNECTOR_ICONS key, a remote image
 * URL (custom connectors with an uploaded/pasted icon), or unknown (falls back
 * to the first letter of `label`).
 */
export function renderConnectorIcon(
  icon: string | undefined,
  className: string,
  label?: string,
): JSX.Element {
  if (isUrl(icon)) {
    return (
      <img
        src={icon}
        alt={label || 'connector'}
        className={`${className} rounded object-contain`}
      />
    );
  }
  if (icon && CONNECTOR_ICONS[icon]) {
    return CONNECTOR_ICONS[icon](className);
  }
  return (
    <div
      className={`flex items-center justify-center rounded bg-gray-200 text-[10px] font-bold text-gray-500 dark:bg-gray-700 dark:text-gray-300 ${className}`}
    >
      {(label || '?')[0]?.toUpperCase()}
    </div>
  );
}
